import { ChangeEvent, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import CloseIcon from "../../../assets/svg/close-icon";
import Button from "../../../components/Button/Button";
import Modal from "../../../components/Modal/Modal";
import { useSendContactUsRequestMutation } from "../../../redux/services/kamApi";
import PhoneNumberCountryCodeSelector from "../../../components/PhoneNumberCountryCodeSelector/PhoneNumberCountryCodeSelector";
import { CountryOption } from "../../../components/PhoneNumberCountryCodeSelector/utils/types";
import {
  sortCountryOptions,
  transformCountryOptions,
} from "../../../components/PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { countryOptions } from "../../../utils/constants/countryOptions";
import { customToast } from "../../../utils/custom-toast/custom-toast";

interface ContactUsFormProps {
  open: boolean;
  onClose: () => void;
}

type ContactUsFormState = {
  email: string;
  selectedCountry: CountryOption | null;
  phone: string;
  message: string;
};

export const ContactUsForm = ({ open, onClose }: ContactUsFormProps) => {
  const transformedCountryOptions = useMemo(() => {
    return sortCountryOptions(transformCountryOptions(countryOptions));
  }, []);

  const defaultCountry = useMemo(() => {
    return (
      transformedCountryOptions.find((country) => country.flag === "gb") ||
      transformedCountryOptions[0] ||
      null
    );
  }, [transformedCountryOptions]);

  const [sendContactUsRequest, { isLoading }] = useSendContactUsRequestMutation();

  const formik = useFormik<ContactUsFormState>({
    initialValues: {
      email: "",
      selectedCountry: defaultCountry,
      phone: "",
      message: "",
    },
    validateOnMount: true,
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email("Enter a valid email")
        .required("Email is required"),
      selectedCountry: Yup.mixed<CountryOption>()
        .nullable()
        .required("Country code is required"),
      phone: Yup.string()
        .trim()
        .required("Phone number is required")
        .matches(/^[0-9()\-\s]{6,20}$/, "Enter a valid phone number"),
      message: Yup.string().trim().required("Message is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const sanitizedPhone = values.phone.replace(/\D+/g, "");
      const dialCode = values.selectedCountry?.dialCode || "";
      const phonePayloadValue = `${dialCode}${sanitizedPhone}`;

      try {
        await sendContactUsRequest({
          email: values.email.trim(),
          phone: phonePayloadValue,
          message: values.message.trim(),
        }).unwrap();
        customToast("Contact request submitted successfully", "success");
        resetForm();
        onClose();
      } catch (error) {
        const errorMessage =
          (error as { data?: { detail?: string; message?: string } })?.data
            ?.detail ||
          (error as { data?: { detail?: string; message?: string } })?.data
            ?.message ||
          "Failed to submit contact us request";
        customToast(errorMessage, "error");
      }
    },
  });

  const emailValue = formik.values.email;
  const selectedCountryValue = formik.values.selectedCountry;
  const phoneValue = formik.values.phone;
  const messageValue = formik.values.message;

  const isSubmitDisabled = useMemo(() => {
    return isLoading || !formik.isValid;
  }, [formik.isValid, isLoading]);

  const handleResetForm = () => {
    formik.resetForm({
      values: {
      email: "",
      selectedCountry: defaultCountry,
      phone: "",
      message: "",
      },
    });
  };

  const handleCloseForm = () => {
    handleResetForm();
    onClose();
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    formik.setFieldValue("email", nextValue);
  };

  const handleCountryCodeChange = (country: CountryOption) => {
    formik.setFieldValue("selectedCountry", country);
    formik.setFieldTouched("selectedCountry", true);
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    formik.setFieldValue("phone", nextValue);
  };

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    formik.setFieldValue("message", nextValue);
  };

  const handleModalOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleCloseForm();
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleModalOpenChange}
      showHeader={false}
      showCloseButton={false}
      contentClassName="bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk rounded-lg w-[460px] shadow-popper-blur-effect backdrop-blur-2xl !z-[99999999]"
      overlayClassName="!bg-black/60 !z-[9999999]"
      bodyClassName="!px-6 !py-5"
    >
      <form className="flex flex-col gap-4" onSubmit={formik.handleSubmit}>
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
              How can VerbaFlo help you?
            </span>
            <span className="text-sm text-data dark:text-data-dk">
              Please write your message below. We will respond as soon as
              possible.
            </span>
          </div>
          <button
            type="button"
            className="w-[30px] h-[30px] bg-gray-100 dark:bg-gray-100-dk rounded-full flex items-center justify-center"
            onClick={handleCloseForm}
          >
            <CloseIcon className="w-[12px] h-[12px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            Email Id
          </label>
          <input
            type="email"
            value={emailValue}
            onChange={handleEmailChange}
            onBlur={formik.handleBlur}
            name="email"
            placeholder="name@example.com"
            className="h-[44px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg px-3 text-md text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none"
          />
          {formik.touched.email && formik.errors.email && (
            <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
              {formik.errors.email}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <PhoneNumberCountryCodeSelector
            label="Phone Number"
            labelClassName="text-md font-medium text-heading-text dark:text-heading-text-dk"
            countryOptions={transformedCountryOptions}
            selectedCountry={selectedCountryValue}
            onCountryChange={handleCountryCodeChange}
            phoneInput={phoneValue}
            onPhoneInputChange={handlePhoneChange}
            validationError={
              formik.touched.phone || formik.submitCount > 0
                ? formik.errors.phone
                : undefined
            }
            onBlur={() => formik.setFieldTouched("phone", true)}
            name="phone"
            countryDropdownClassName="w-[280px] min-w-[280px] max-h-[250px]"
            countryDropdownSelectedOptionClassName="h-[44px] px-2.5 py-[10px] rounded-lg border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk"
            phoneInputClassName="h-[44px] px-3 rounded-lg bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            Your message
          </label>
          <textarea
            value={messageValue}
            onChange={handleMessageChange}
            onBlur={formik.handleBlur}
            name="message"
            placeholder="Tell us if what do you need assistance with or request a new feature, modality or WorkFlo use case..."
            className="h-[140px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg py-3 px-3 text-md text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk resize-none outline-none"
          />
          {formik.touched.message && formik.errors.message && (
            <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
              {formik.errors.message}
            </span>
          )}
        </div>

        <div className="flex gap-3 items-center justify-end pt-2">
          <Button
            innerText="Cancel"
            buttonContainerClass="w-[100px] h-[32px] border border-border-gray dark:border-border-gray-dk rounded-lg"
            innerTextClass="text-md font-semibold text-heading-text dark:text-heading-text-dk"
            onClick={handleCloseForm}
            disabled={isLoading}
          />
          <Button
            type="submit"
            innerText={isLoading ? "Submitting..." : "Submit"}
            buttonContainerClass={`w-[100px] h-[32px] rounded-lg ${
              isSubmitDisabled
                ? "bg-gray-100 dark:bg-gray-100-dk border border-border-gray dark:border-border-gray-dk opacity-50 cursor-not-allowed"
                : "bg-verba-indigo dark:bg-pure-white dark:border dark:border-border-gray-dk"
            }`}
            innerTextClass={`text-md font-semibold ${
              isSubmitDisabled
                ? "text-gray-placeholder dark:text-gray-placeholder-dk"
                : "text-fixed-white dark:text-[#1a1a1a]"
            }`}
            isLoading={isLoading}
            disabled={isSubmitDisabled}
            loaderColor={isSubmitDisabled ? "text-gray-placeholder dark:text-gray-placeholder-dk" : "text-fixed-white dark:text-[#1a1a1a]"}
            loaderSize={14}
          />
        </div>
      </form>
    </Modal>
  );
};
